import { computed, Directive, effect, inject, input, signal, Signal } from '@angular/core';
import { buildConf, mapRows } from '../../../shared/table-config';
import { PaginationStore } from '../../../../store/signal-stores/pagination.signal-store';
import { BaseColumn } from '../../../../models/dsy-table.model';

@Directive()
export abstract class BaseTableComponent<Row extends object, ColumnKey extends string> {
  readonly rows = input<readonly Row[]>([]);

  readonly paginationStore = inject(PaginationStore, { optional: true });

  readonly sortColumn = signal<ColumnKey | null>(null);
  readonly sortDirection = signal<'asc' | 'desc' | null>(null);

  readonly columnsSignal: Signal<readonly BaseColumn<Row, ColumnKey>[]>;

  constructor(columnsSignal: Signal<readonly BaseColumn<Row, ColumnKey>[]>) {
    this.columnsSignal = columnsSignal;

    let previousRows: readonly Row[] | null = null;
    effect(() => {
      const rows = this.rows();
      const hadRows = previousRows !== null && previousRows.length > 0;
      previousRows = rows;

      if (hadRows) {
        this.paginationStore?.resetPageIndex();
        this.sortColumn.set(null);
        this.sortDirection.set(null);
      }
    });
  }

  readonly processedRows = computed(() => {
    const data = this.rows();
    const sortedData = this.sortDataRows(data);
    if (this.paginationStore) {
      return this.paginationStore.getPagedData(sortedData);
    }
    return sortedData;
  });

  readonly tableDataSignal = computed(() => mapRows<Row, ColumnKey>(this.processedRows(), this.columnsSignal()));

  readonly tableConfSignal = computed(() =>
    buildConf<Row, ColumnKey>(
      this.columnsSignal(),
      this.processedRows().map((r: any) => r.id || '')
    )
  );

  getHeaderIconColor(columnKey: ColumnKey): string {
    if (this.sortColumn() === columnKey) {
      return this.sortDirection() === 'asc' ? '#00b300' : '#ff0606';
    }
    return '#000000';
  }

  sortBy(columnKey: ColumnKey): void {
    if (this.sortColumn() === columnKey) {
      if (this.sortDirection() === 'asc') {
        this.sortDirection.set('desc');
      } else if (this.sortDirection() === 'desc') {
        this.sortDirection.set(null);
        this.sortColumn.set(null);
      } else {
        this.sortDirection.set('asc');
      }
    }else{
      this.sortColumn.set(columnKey);
      this.sortDirection.set('asc');
    }
  }

  private sortDataRows(data: readonly Row[]){
    const sortColumn = this.sortColumn();
    const sortDirection = this.sortDirection();
    const column = this.columnsSignal().find(c => c.key === sortColumn);

    if(!sortColumn || !sortDirection || !column){
      return data;
    }

    const sortedData = [...data].sort((a, b) => {
          if (column.sortFunction) {
            return column.sortFunction(a, b, sortDirection);
          }
          return 0;
        });

    return sortedData;
  }
}
